import { csvTemplate, isBlankRow, mergeRows, parseListCsv } from '@portal/forms';
import { useId, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { settledPhone } from './settled-phone';

/** A blank row for a list: text boxes empty, checkboxes unticked. */
export function blankRow(hint) {
  return Object.fromEntries(
    (hint.columns ?? []).map((column) => [column.name, column.widget === 'checkbox' ? false : '']),
  );
}

/** The input a column's widget draws inside a cell. */
function inputType(widget) {
  switch (widget) {
    case 'email':
      return 'email';
    case 'tel':
      return 'tel';
    case 'date':
      return 'date';
    case 'checkbox':
      return 'checkbox';
    default:
      return 'text';
  }
}

/**
 * A `list` field: a table with one row of boxes per entry, rows added by hand or from a
 * CSV file, and a template to download so the CSV has the right columns.
 *
 * The rows live in react-hook-form's field array, so validation, errors and submission
 * work exactly as for any other field: the schema sees an array of objects. Nothing
 * about the CSV leaves the browser — the file is read here, the rows go into the form,
 * and the form is what gets submitted.
 *
 * An upload is a merge, not an append: the same file twice changes nothing, a corrected
 * file updates the rows it corrects, and rows with mistakes still come in — validated
 * on the spot, so the mistake is shown on the cell and fixed there, not in the file.
 */
export function ListField({
  hint,
  control,
  register,
  setValue,
  getValues,
  trigger,
  errors,
  disabled,
  rowSchema,
}) {
  const name = hint.name;
  const columns = hint.columns ?? [];
  const { min, max, rowLabel } = hint.list ?? { min: 0, max: Infinity, rowLabel: 'row' };
  const { fields, append, remove, replace } = useFieldArray({ control, name });
  const [importReport, setImportReport] = useState(undefined);
  const id = useId();
  const helpId = hint.helpText ? `${id}-help` : undefined;
  const listError = errors?.[name]?.root?.message ?? errors?.[name]?.message;
  const errorId = listError ? `${id}-error` : undefined;
  const plural = (n) => (n === 1 ? rowLabel : `${rowLabel}s`);

  function cellError(index, column) {
    return errors?.[name]?.[index]?.[column.name]?.message;
  }

  /** Registration for one cell; a phone cell is tidied to E.164 as the user leaves it. */
  function registerCell(index, column) {
    const path = `${name}.${index}.${column.name}`;
    if (column.widget !== 'tel') return register(path);
    return register(path, {
      onBlur: (event) => {
        const settled = settledPhone(event.target.value);
        if (settled !== event.target.value) {
          setValue(path, settled, { shouldDirty: true, shouldValidate: true });
        }
      },
    });
  }

  function downloadTemplate() {
    const blob = new Blob([csvTemplate(hint)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${name}-template.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  async function importFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const text = await file.text();
    const parsed = parseListCsv(hint, rowSchema, text);
    if (parsed.rows.length === 0) {
      setImportReport({
        file: file.name,
        added: 0,
        updated: 0,
        unchanged: 0,
        issues: parsed.issues,
      });
      return;
    }

    // Merge into what the form holds now (not the field array's snapshot), keep to the
    // limit, and validate straight away so an imported mistake is shown on its cell.
    const held = getValues(name) ?? [];
    const blankDropped = held.filter((row) => isBlankRow(columns, row)).length;
    const merged = mergeRows(hint, held, parsed.rows);
    const skipped = Math.max(0, merged.rows.length - max);
    replace(merged.rows.slice(0, max));
    setImportReport({
      file: file.name,
      added: merged.added - skipped,
      updated: merged.updated,
      unchanged: merged.unchanged,
      skipped,
      blankDropped,
      issues: parsed.issues,
    });
    await trigger(name);
  }

  const canAdd = !disabled && fields.length < max;

  return (
    <div className={`field field--list${listError ? ' field--invalid' : ''}`}>
      <div className="list-head">
        <span className="list-label" id={`${id}-label`}>
          {hint.label}
        </span>
        <span className="list-count muted">
          {fields.length} of {max}
        </span>
      </div>
      {hint.helpText && (
        <p id={helpId} className="help">
          {hint.helpText}
        </p>
      )}

      <div className="list-scroll">
        <table className="list-table" aria-labelledby={`${id}-label`}>
          <thead>
            <tr>
              <th scope="col" className="list-index">
                #
              </th>
              {columns.map((column) => (
                <th scope="col" key={column.name}>
                  {column.label}
                </th>
              ))}
              <th scope="col">
                <span className="visually-hidden">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((row, index) => (
              <tr key={row.id}>
                <td className="list-index">{index + 1}</td>
                {columns.map((column) => {
                  const error = cellError(index, column);
                  const cellId = `${id}-${index}-${column.name}`;
                  return (
                    <td key={column.name} className={error ? 'cell--invalid' : undefined}>
                      <input
                        id={cellId}
                        type={inputType(column.widget)}
                        aria-label={`${column.label}, ${rowLabel} ${index + 1}`}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? `${cellId}-error` : undefined}
                        placeholder={column.placeholder}
                        disabled={disabled}
                        {...registerCell(index, column)}
                      />
                      {error && (
                        <p id={`${cellId}-error`} className="error" role="alert">
                          {error}
                        </p>
                      )}
                    </td>
                  );
                })}
                <td>
                  <button
                    type="button"
                    className="link"
                    disabled={disabled}
                    aria-label={`Remove ${rowLabel} ${index + 1}`}
                    onClick={() => remove(index)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="list-actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={!canAdd}
          onClick={() => append(blankRow(hint))}
        >
          Add {rowLabel}
        </button>
        <button type="button" className="link" onClick={downloadTemplate}>
          Download CSV template
        </button>
        <label className={`link${disabled ? ' link--disabled' : ''}`}>
          Upload CSV
          <input
            type="file"
            accept=".csv,text/csv"
            className="visually-hidden"
            disabled={disabled}
            onChange={importFile}
          />
        </label>
      </div>

      {importReport && (
        <div className="list-report" role="status">
          <p>
            From <code>{importReport.file}</code>: {importReport.added} {plural(importReport.added)}{' '}
            added, {importReport.updated} updated, {importReport.unchanged} already present
            {importReport.skipped > 0 &&
              `; ${importReport.skipped} left out because the list holds at most ${max}`}
            {importReport.blankDropped > 0 &&
              `; ${importReport.blankDropped} empty ${plural(importReport.blankDropped)} dropped`}
            .
            {importReport.issues.length > 0 &&
              ` ${importReport.issues.length} ${importReport.issues.length === 1 ? 'row needs' : 'rows need'} a correction — see the marked boxes.`}
          </p>
          {importReport.issues.length > 0 && (
            <ul className="error">
              {importReport.issues.map((issue) => (
                <li key={`${issue.line}-${issue.message}`}>
                  Line {issue.line}: {issue.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {listError && (
        <p id={errorId} className="error" role="alert">
          {listError}
        </p>
      )}
      {min > 0 && fields.length === 0 && !listError && (
        <p className="help">
          Add at least {min === 1 ? `one ${rowLabel}` : `${min} ${rowLabel}s`}.
        </p>
      )}
    </div>
  );
}
